import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getSubjectList } from '../../redux/sclassRelated/sclassHandle';
import { getUserDetails } from '../../redux/userRelated/userHandle';
import { 
    Container, Typography, Box, Paper, Grid, 
    Avatar, Chip, Button, Stack, CircularProgress, 
    Fade, Zoom, Tooltip, Divider, IconButton
} from '@mui/material';
import { 
    School as SchoolIcon, 
    MenuBook as SubjectIcon, 
    Person as TeacherIcon, 
    Assignment as SyllabusIcon,
    NavigateNext as ArrowIcon,
    CheckCircle as AssignedIcon,
    Warning as UnassignedIcon,
    Assessment as StatsIcon,
    History as HistoryIcon,
    ArrowForward as ViewIcon
} from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 100px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const SubjectCard = styled(Paper)`
    padding: 24px;
    border-radius: 24px;
    background: white;
    border: 1px solid #e2e8f0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    cursor: pointer;
    
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 4px;
        height: 100%;
        background: ${props => props.color || '#6366f1'};
    }
    
    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        border-color: ${props => props.color || '#6366f1'}40;
    }
`;

const StatBadge = styled(Paper)`
    padding: 24px;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: white;
    border: 1px solid #eef2ff;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -10px rgba(99, 102, 241, 0.15);
    }
`;

const TeacherTag = styled(Box)`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-radius: 12px;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
`;

const StudentSubjects = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { subjectsList, sclassDetails, loading: subjectsLoading } = useSelector((state) => state.sclass);
    const { currentUser, loading: userLoading } = useSelector((state) => state.user);

    useEffect(() => {
        dispatch(getUserDetails(currentUser._id, "Student"));
        dispatch(getSubjectList(currentUser?.sclassName?._id, "ClassSubjects"));
    }, [dispatch, currentUser]);

    const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

    const stats = useMemo(() => {
        const total = subjectsList?.length || 0;
        const withTeachers = subjectsList?.filter(s => s.teacher)?.length || 0;
        return { total, withTeachers, pending: total - withTeachers };
    }, [subjectsList]);

    const navigateToNotes = (subjectName) => {
        navigate('/Student/notes', { state: { selectedSubject: subjectName } });
    };

    const navigateToResults = (subjectName) => {
        navigate('/Student/results', { state: { filterBySubject: subjectName } });
    };

    if (subjectsLoading || userLoading) {
        return (
            <Box sx={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={50} thickness={4} />
            </Box>
        );
    }

    const className = sclassDetails?.sclassName || currentUser?.sclassName?.sclassName || 'N/A';

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="xl">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                                    <SchoolIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                        Subjects
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                        Explore your subjects and assigned faculty for Class {className}.
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="xl" sx={{ mt: 4 }}>
                <Grid container spacing={4}>
                    {/* Stats & Class Info Sidebar */}
                    <Grid item xs={12} md={3}>
                        <Stack spacing={3}>
                            <StatBadge elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6' }}><SubjectIcon /></Avatar>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>TOTAL SUBJECTS</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.total} Courses</Typography>
                                    </Box>
                                </Box>
                            </StatBadge>

                            <StatBadge elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ bgcolor: '#ecfdf5', color: '#10b981' }}><AssignedIcon /></Avatar>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>FACULTY ASSIGNED</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.withTeachers} Teachers</Typography>
                                    </Box>
                                </Box>
                            </StatBadge>

                            {stats.pending > 0 && (
                                <StatBadge elevation={0}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: '#fff7ed', color: '#f59e0b' }}><UnassignedIcon /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>PENDING FACULTY</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.pending} Subjects</Typography>
                                        </Box>
                                    </Box>
                                </StatBadge>
                            )}

                            <Paper sx={{ p: 3, borderRadius: '24px', bgcolor: '#1e293b', color: 'white' }}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                    <StatsIcon sx={{ color: '#6366f1' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Enrolled Level</Typography>
                                </Stack>
                                <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>Grade {className}</Typography>
                                <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', my: 2 }} />
                                <Stack spacing={1.5}> 
                                </Stack>
                            </Paper>
                        </Stack>
                    </Grid>

                    {/* Subject Grid Feed */}
                    <Grid item xs={12} md={9}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <Avatar sx={{ bgcolor: '#f5f3ff', color: '#8b5cf6' }}><SubjectIcon /></Avatar>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Subject Roadmap</Typography>
                        </Box>

                        <Grid container spacing={3}>
                            {subjectsList?.length === 0 ? (
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 10, textAlign: 'center', borderRadius: '24px', border: '1px dashed #cbd5e1' }}>
                                        <Typography variant="h6" color="textSecondary">No subjects found for this class.</Typography>
                                    </Paper>
                                </Grid>
                            ) : (
                                subjectsList.map((subject, index) => (
                                    <Grid item xs={12} sm={6} key={subject._id}>
                                        <Zoom in timeout={300 + index * 50}>
                                            <SubjectCard 
                                                elevation={0} 
                                                color={colors[index % colors.length]}
                                                onClick={() => navigateToNotes(subject.subName)}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                                    <Box>
                                                        <Chip 
                                                            label={subject.subCode || 'CODE-NA'} 
                                                            size="small" 
                                                            sx={{ 
                                                                bgcolor: `${colors[index % colors.length]}15`, 
                                                                color: colors[index % colors.length], 
                                                                fontWeight: 800, 
                                                                borderRadius: '8px',
                                                                mb: 1
                                                            }} 
                                                        />
                                                        <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b' }}>
                                                            {subject.subName}
                                                        </Typography>
                                                    </Box>
                                                    <Avatar sx={{ bgcolor: '#f8fafc', color: colors[index % colors.length] }}>
                                                        <SubjectIcon />
                                                    </Avatar>
                                                </Box>

                                                <TeacherTag>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: subject.teacher ? colors[index % colors.length] : '#94a3b8' }}>
                                                        <TeacherIcon sx={{ fontSize: 18 }} />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>FACULTY IN-CHARGE</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                                            {subject.teacher?.name || 'Not Assigned Yet'}
                                                        </Typography>
                                                    </Box>
                                                    {subject.teacher && (
                                                        <Tooltip title="Assigned">
                                                            <AssignedIcon sx={{ color: '#10b981', fontSize: 16, ml: 'auto' }} />
                                                        </Tooltip>
                                                    )}
                                                </TeacherTag>

                                                <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                                                    <Button 
                                                        fullWidth 
                                                        variant="contained" 
                                                        size="small"
                                                        startIcon={<ViewIcon />}
                                                        onClick={(e) => { e.stopPropagation(); navigateToResults(subject.subName); }}
                                                        sx={{ 
                                                            borderRadius: '12px', 
                                                            textTransform: 'none', 
                                                            fontWeight: 700, 
                                                            bgcolor: '#1e293b',
                                                            '&:hover': { bgcolor: '#0f172a' }
                                                        }}
                                                    >
                                                        View Results
                                                    </Button>
                                                    <Tooltip title="Study Materials">
                                                        <IconButton 
                                                            onClick={(e) => { e.stopPropagation(); navigateToNotes(subject.subName); }}
                                                            sx={{ bgcolor: '#f1f5f9', borderRadius: '12px' }}
                                                        >
                                                            <SyllabusIcon sx={{ color: '#64748b' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </SubjectCard>
                                        </Zoom>
                                    </Grid>
                                ))
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </ContentContainer>
        </PageWrapper>
    );
};

export default StudentSubjects;
